import { useEffect, useState } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import {
    AssistantAutoCompleteResult,
    AssistantSuggestion,
    AssistantSuggestionType,
    useRepositoryQuery,
} from '@mews-commander/data-access';
import { useLanguageCode } from '@mews-commander/core';
import { getCurrentEnterpriseChainId } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { useCommanderSelector } from '@mews-commander/utils';
import { startsWithMqlTriggerCharacter } from '../searchBar/MqlHelper';

export const useAssistantAutocompleteQuery = (
    searchTerm: string,
    enterpriseIds: Array<string>,
    assistantAutocompleteEnabled: boolean
) => {
    const chainId = useCommanderSelector<string>(getCurrentEnterpriseChainId);
    const languageCode = useLanguageCode();

    const firstCharactersIsMQLCommand = startsWithMqlTriggerCharacter(searchTerm);
    const enabled = assistantAutocompleteEnabled && searchTerm.length > 0 && firstCharactersIsMQLCommand;
    const trimmedSearchTerm = (
        firstCharactersIsMQLCommand ? searchTerm.substring(1, searchTerm.length) : searchTerm
    ).trimEnd();

    const queryResult = useRepositoryQuery(
        'assistantAutocompletion',
        'autocomplete',

        {
            searchToken: trimmedSearchTerm,
            chainId,
            enterpriseIds,
            languageCode,
        },
        {
            enabled,
            gcTime: 1,
            placeholderData: keepPreviousData,
            select: transform,
        }
    );

    const [history, setHistory] = useState<UseAssistantAutocompleteQueryResult>(
        {} as UseAssistantAutocompleteQueryResult
    );

    useEffect(() => {
        if (queryResult.data) {
            setHistory((prevHistory) => {
                const mergedSuggestions = removeDuplicates([
                    ...(prevHistory.suggestionsHistory ?? []),
                    ...queryResult.data.allSuggestions,
                ]);

                return {
                    allSuggestions: queryResult.data.allSuggestions,
                    suggestionsHistory: mergedSuggestions,
                    commands: queryResult.data.commands,
                    commandOperators: queryResult.data.commandOperators,
                    entities: queryResult.data.entities,
                    parameters: queryResult.data.parameters,
                    operators: queryResult.data.operators,
                    values: queryResult.data.values,
                    texts: queryResult.data.texts,
                    hasResults: queryResult.data.hasResults,
                };
            });
        }
    }, [queryResult.data]);

    return { ...queryResult, data: history };
};

interface UseAssistantAutocompleteQueryResult {
    hasResults: boolean;
    allSuggestions: AssistantSuggestion[];
    suggestionsHistory: AssistantSuggestion[];
    commands: AssistantSuggestion[];
    commandOperators: AssistantSuggestion[];
    entities: AssistantSuggestion[];
    parameters: AssistantSuggestion[];
    operators: AssistantSuggestion[];
    values: AssistantSuggestion[];
    texts: AssistantSuggestion[];
}

const transform = (data: AssistantAutoCompleteResult): UseAssistantAutocompleteQueryResult => {
    const suggestions = data?.autoSuggestions ?? [];
    // In order to prevent the ui to see the spaces in a keyword such as a value (LastName, First Name), we replace the spaces by none breaking spaces.
    suggestions.forEach((suggestion) => {
        suggestion.text = replaceSpacesByNoneBreakingSpaces(suggestion.text);
    });
    const commands = filterSuggestionsByType(suggestions, AssistantSuggestionType.Command);
    const commandOperators = filterSuggestionsByType(suggestions, AssistantSuggestionType.CommandOperator);
    const entities = filterSuggestionsByType(suggestions, AssistantSuggestionType.Entity);
    const parameters = filterSuggestionsByType(suggestions, AssistantSuggestionType.Parameter);
    const operators = filterSuggestionsByType(suggestions, AssistantSuggestionType.Operator);
    const values = filterSuggestionsByType(suggestions, AssistantSuggestionType.Value);
    const texts = filterSuggestionsByType(suggestions, AssistantSuggestionType.Text);
    return {
        hasResults: suggestions.length > 1,
        allSuggestions: [
            ...commands,
            ...commandOperators,
            ...entities,
            ...parameters,
            ...operators,
            ...values,
            ...texts,
        ],
        suggestionsHistory: [],
        commands,
        commandOperators,
        entities,
        parameters,
        operators,
        values,
        texts,
    };
};

const filterSuggestionsByType = (
    suggestions: AssistantSuggestion[],
    type: AssistantSuggestionType
): AssistantSuggestion[] => suggestions?.filter((suggestion) => suggestion.type === type) ?? [];

const replaceSpacesByNoneBreakingSpaces = (value: string): string => value.replace(/\s/g, '\u00A0');

const removeDuplicates = (suggestions: AssistantSuggestion[]): AssistantSuggestion[] =>
    suggestions.reduce((acc: AssistantSuggestion[], suggestion: AssistantSuggestion) => {
        if (!acc.some((accSuggestion) => accSuggestion.text === suggestion.text)) {
            acc.push(suggestion);
        }
        return acc;
    }, []);
